/**
 * Sales Teams & Lists endpoints (`docs/UTILITIES.md` §9), through the one
 * request path in `lib/api.ts` so errors arrive as the same `ApiError`.
 */

import { request } from '../../lib/api'

export const BASE = '/api/utilities/sales-lists'

export type ListType = 'sales_team' | 'email_list'

export interface Member {
  readonly member: string
  readonly member_email: string | null
  readonly recipient: string | null
}

export interface PortalList {
  readonly list_id: number | null
  readonly list_type: ListType
  readonly name: string
  readonly owner_name: string | null
  readonly owner_email: string | null
  readonly attributes: Readonly<Record<string, string>>
  readonly members: readonly Member[]
  readonly updated_at: string | null
  readonly updated_by: string | null
}

export interface Landing {
  readonly slug: string
  readonly lists: readonly PortalList[]
  readonly reps: readonly string[]
  readonly list_types: readonly ListType[]
  readonly schedules: readonly { value: string; label: string }[]
}

function body(list: PortalList): string {
  return JSON.stringify({
    list_type: list.list_type,
    name: list.name,
    owner_name: list.owner_name,
    owner_email: list.owner_email,
    attributes: list.attributes,
    members: list.members,
    expected_updated_at: list.updated_at,
  })
}

/** Scheduled email (`docs/UTILITIES.md` §10): this server's sending settings. */
export type MailMode = 'off' | 'test' | 'live'

export interface MailJob {
  readonly key: string
  readonly title: string
  readonly about: string
  readonly list_name: string | null
  readonly mode: MailMode
  readonly test_to: string | null
  readonly reply_to: string | null
  readonly business_day: number | null
  readonly extra_cc: string
  readonly schedule: string | null
  readonly next_send: string | null
  readonly to: readonly string[] | null
  readonly cc: readonly string[] | null
}

export interface MailSendRow {
  readonly id: number
  readonly job: string
  readonly occurrence: string
  readonly mode: MailMode
  readonly recipient: string
  readonly trigger: 'schedule' | 'manual'
  readonly status: 'sending' | 'sent' | 'failed' | 'skipped'
  readonly to: string | null
  readonly cc: string | null
  readonly subject: string | null
  readonly detail: string | null
  readonly created_at: string
  readonly finished_at: string | null
}

export interface MailOverview {
  readonly configured: boolean
  readonly sender: string | null
  readonly timezone: string
  readonly send_hour: number
  readonly now: string
  readonly lists_error: string | null
  readonly jobs: readonly MailJob[]
  readonly sends: readonly MailSendRow[]
}

export interface MailSettings {
  readonly mode: MailMode
  readonly test_to: string | null
  readonly reply_to: string | null
  readonly business_day: number | null
  readonly extra_cc: string | null
}

export interface SendResult {
  readonly sent: number
  readonly failed: number
  readonly skipped: number
  readonly problems: readonly string[]
}

export const mailApi = {
  overview: () => request<MailOverview>(`${BASE}/mail`),
  save: (key: string, settings: MailSettings) =>
    request<MailSettings>(`${BASE}/mail/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
  sendNow: (key: string) =>
    request<SendResult>(`${BASE}/mail/${encodeURIComponent(key)}/send`, { method: 'POST' }),
}

export const listsApi = {
  landing: () => request<Landing>(BASE),
  save: (list: PortalList) =>
    list.list_id === null
      ? request<{ list: PortalList }>(BASE, { method: 'POST', body: body(list) })
      : request<{ list: PortalList }>(`${BASE}/${String(list.list_id)}`, {
          method: 'PUT',
          body: body(list),
        }),
  remove: (list: PortalList) =>
    request<{ deleted: number }>(`${BASE}/${String(list.list_id)}/delete`, {
      method: 'POST',
      body: JSON.stringify({ expected_updated_at: list.updated_at }),
    }),
}
